import { Box, Skeleton, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { ChangeEvent, useMemo, useRef, useState } from 'react';
import styles from './ImgUpload.module.css';

interface ImgUploadProps {
  loading?: boolean;
  error?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  progress?: number;
  disabled?: boolean;
  value?: string;
  name?: string;
  required?: boolean;
}

type uiState = 'default' | 'error' | 'disabled' | 'loading' | 'downloading' | 'uploaded';

const ImgUpload: React.FC<ImgUploadProps> = ({ loading, onChange, disabled, value, error, required, name }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [downloading, setDownloading] = useState(!!value);

  const onLoadImg = () => {
    setDownloading(false);
  };

  const state: uiState = useMemo(() => {
    if (loading) return 'loading';
    if (error) return 'error';
    if (disabled) return 'disabled';
    if (value && downloading) return 'downloading';
    if (value) return 'uploaded';
    return 'default';
  }, [loading, error, disabled, value, downloading]);

  const clickable = !disabled && !loading;

  console.log('state', state);

  return (
    <Box className={styles[`ImgUpload--${state}`]} onClick={() => clickable && fileInputRef.current?.click()}>
      <input
        name={name}
        type="file"
        required={value ? false : required}
        onChange={onChange}
        ref={fileInputRef}
        id="file-upload"
        tabIndex={0}
        role="button"
        className={styles.ImgUpload_input}
        accept="image/*"
      />

      {state === 'loading' && (
        <Skeleton className={styles['ImgUpload_loading']} variant="rectangular" height={186} sx={{ borderRadius: 2 }} />
      )}

      {!loading && (
        <Box>
          {!error && !value && !disabled && (
            <>
              <Icon icon="mdi:upload" width="48" height="48" />
              <Typography variant="h3" sx={{ mb: 1 }}>
                Upload Image
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Click to select a file
              </Typography>
            </>
          )}
          {error && (
            <Box className={styles.error}>
              <Icon icon="mdi:alert-circle" width="48" height="48" />
              <Typography variant="h3" sx={{ mb: 1 }}>
                Error
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {error}
              </Typography>
            </Box>
          )}

          {value && (
            <>
              {downloading && (
                <Skeleton variant="rectangular" height={186} sx={{ width: '100%', borderRadius: 2, mb: 2 }} />
              )}
              <img className={styles['ImgUpload_img']} onLoad={onLoadImg} src={value} alt="Uploaded" />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ImgUpload;
